import type { NavigateFunction } from 'react-router-dom'
import type { OnboardingStatus } from '@/hooks/use-onboarding'
import {
  countedOnboardingSteps,
  employerAccountOnboardingSteps,
  WELCOME_STEP_ID,
} from '@/lib/onboarding-display'
import { firstIncompleteCountedStepId } from '@/lib/onboarding-setup-list-copy'

type CompleteStepFn = (stepId: string) => Promise<{ success: boolean }>

export function onboardingResumePath(
  status: Pick<OnboardingStatus, 'steps' | 'completed_step_ids' | 'group_name'>,
  completedStepIds: string[],
): string {
  const accountSteps = employerAccountOnboardingSteps(status.steps || [], status.group_name)
  const countedSteps = countedOnboardingSteps(accountSteps)
  const targetStepId = firstIncompleteCountedStepId(countedSteps, completedStepIds)
  if (targetStepId) {
    return `/onboarding?step=${encodeURIComponent(targetStepId)}`
  }
  return '/onboarding'
}

/** Mark welcome acknowledged when launching onboarding from home or profile. */
export async function ensureWelcomeComplete(
  completedStepIds: string[],
  completeStep: CompleteStepFn,
): Promise<string[]> {
  if (completedStepIds.includes(WELCOME_STEP_ID)) {
    return completedStepIds
  }

  const result = await completeStep(WELCOME_STEP_ID)
  if (!result.success) {
    return completedStepIds
  }

  return [...completedStepIds, WELCOME_STEP_ID]
}

export async function beginOnboardingFromLauncher(
  status: OnboardingStatus,
  completeStep: CompleteStepFn,
  navigate: NavigateFunction,
): Promise<void> {
  const completedStepIds = await ensureWelcomeComplete(
    status.completed_step_ids ?? [],
    completeStep,
  )
  navigate(onboardingResumePath(status, completedStepIds))
}
